import moment, { Moment } from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import Select, {
	SelectValue,
} from '../../../../../Components/Base/Select/Select';
// @ts-ignore
import styles from './BirthDatePicker.module.scss';
import Label from '../../../../../Components/Base/Label/Label';
import useTranslation from '../../../../../Hooks/useTranslation';
import { useAppContext } from '../../../../../Services/AppContext';

interface Props {
	value: string;
	onChange?: (value: string) => void;
}

const pad = (number: string) => {
	return number.length < 2 ? '0' + number : number;
};

const prepareSelectObject = (numb: number) => {
	const inString = numb.toString();
	return {
		label: pad(inString),
		value: inString,
	};
};

const BirthDatePicker: React.FC<Props> = ({ value, onChange }) => {
	const { t } = useTranslation();
	const [day, setDay] = useState<string | null>();
	const [month, setMonth] = useState<string | null>();
	const [year, setYear] = useState<string | null>();

	const days = useMemo(() => {
		const tempDays: SelectValue[] = [];
		for (let i = 1; i <= 31; i++) {
			tempDays.push(prepareSelectObject(i));
		}
		return tempDays;
	}, []);

	const months = useMemo(() => {
		const tempMonths: SelectValue[] = [];
		for (let i = 1; i <= 12; i++) {
			tempMonths.push(prepareSelectObject(i));
		}
		return tempMonths;
	}, []);

	const years = useMemo(() => {
		const currentYear = moment().year();
		const tempYears: SelectValue[] = [];
		for (let i = currentYear; i >= currentYear - 118; i--) {
			tempYears.push(prepareSelectObject(i));
		}
		return tempYears;
	}, []);

	useEffect(() => {
		if (!day || !month || !year) {
			return;
		}
		const date = moment(
			year + '-' + pad(month) + '-' + pad(day) + 'T00:00:00',
		).toString();
		onChange?.(date);
	}, [day, month, year]);

	useEffect(() => {
		const date = moment(value);
		const newDay = date.format('DD');
		if (newDay != day) {
			setDay(newDay);
		}
		const newMonth = date.format('MM');
		if (newMonth != month) {
			setMonth(newMonth);
		}
		const newYear = date.format('YYYY');
		if (newYear != month) {
			setYear(newYear);
		}
	}, [value]);

	const handleChange = (
		index: 'day' | 'month' | 'year',
		value: SelectValue,
	) => {
		switch (index) {
			case 'day':
				setDay(value?.value);
				break;
			case 'month':
				setMonth(value?.value);
				break;
			case 'year':
				setYear(value?.value);
				break;
		}
	};

	const dayVal = useMemo(() => {
		return days.find(item => item.value === day);
	}, [day]);

	const monthVal = useMemo(() => {
		return months.find(item => item.value === month);
	}, [month]);

	const yearVal = useMemo(() => {
		return years.find(item => item.value === year);
	}, [year]);

	return (
		<div className={styles.wrapper}>
			<div className={styles.day}>
				<Label tx='base.days.one' firstUpper />
				<Select
					options={days}
					value={dayVal}
					isSearchable
					placeholder={t('base.shared.pick')}
					onChange={(value: SelectValue) => handleChange('day', value)}
				/>
			</div>
			<div className={styles.month}>
				<Label tx='base.months.one' firstUpper />
				<Select
					options={months}
					value={monthVal}
					isSearchable
					placeholder={t('base.shared.pick')}
					onChange={(value: SelectValue) => handleChange('month', value)}
				/>
			</div>
			<div className={styles.year}>
				<Label tx='base.years.one' firstUpper />
				<Select
					options={years}
					value={yearVal}
					isSearchable
					placeholder={t('base.shared.pick')}
					onChange={(value: SelectValue) => handleChange('year', value)}
				/>
			</div>
		</div>
	);
};

export default BirthDatePicker;
